import {
  deriveRoiBBoxInWGS84,
  getModeledCoastlinesDisplayGeoJSON,
  getModeledCoastlinesGeoJSON,
  padBBox,
  readCoastlineFromMatfile,
} from './matlabParser';
import { parseCoastlineCsvText } from './csvParser';
import { resolveCrsToProj4 } from './reprojection';

const isCsvFile = (file) => /\.csv$/i.test(file.name || '') || file.type === 'text/csv';

/**
 * @function parseShoreSModelFile
 * @description Parses a model output file, either a `.mat` or a long-format `time,x,y` CSV, into
 * the shared coastline/ROI contract the rest of Shore-S consumes. This is the single entry point
 * `ShoreSDataChooser.jsx` calls.
 * @param {File} file
 * @param {string} crs - The CRS the model's x/y coordinates are expressed in.
 * **Called by:
 * @see {ShoreSDataChooser}
 * @memberof module:ShoreS
 * @returns {Promise<{
 *   hasTemporalData: boolean,
 *   dateRange: ({start: string, end: string}|null),
 *   roiBBox: (Array<number>|null),
 *   modelBBox: (Array<number>|null),
 *   modeledCoastlinesGeoJSON: GeoJSON.FeatureCollection,
 *   modeledCoastlinesDisplayGeoJSON: GeoJSON.FeatureCollection,
 *   crs: string
 * }>}
 */
export const parseShoreSModelFile = async (file, crs) => {
  if (!resolveCrsToProj4(crs)) {
    throw new Error(`CRS "${crs}" não reconhecido. Use um código UTM (ex: EPSG:32722) ou EPSG:4326.`);
  }

  const { coastlineByTime, hasTemporalData } = isCsvFile(file)
    ? parseCoastlineCsvText(await file.text())
    : readCoastlineFromMatfile(await file.arrayBuffer());

  const modeledCoastlinesGeoJSON = getModeledCoastlinesGeoJSON(coastlineByTime, crs);

  const modeledCoastlinesDisplayGeoJSON = getModeledCoastlinesDisplayGeoJSON(coastlineByTime, crs);

  const modelBBox = deriveRoiBBoxInWGS84(modeledCoastlinesGeoJSON, crs);
  const roiBBox = modelBBox ? padBBox(modelBBox) : null;


  const times = coastlineByTime.map((entry) => entry.time);
  const dateRange = hasTemporalData
    ? {
        start: times[0].toISOString().slice(0, 10),
        end: times[times.length - 1].toISOString().slice(0, 10),
      }
    : null;

  return {
    hasTemporalData,
    dateRange,
    roiBBox,
    modelBBox,
    modeledCoastlinesGeoJSON,
    modeledCoastlinesDisplayGeoJSON,
    crs,
  };
};
